import React, { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useCommission } from "../../services/customHooks/useCommission";
import { parseDynamicFareRules } from "../../constants/helper";
import { useNavigate } from "react-router-dom";
import { useGlobalState } from "../../services/context/AuthContext";

interface FlightCardProps {
  flightData?: any; // Make it optional
  isActive?: boolean;
  onToggle: () => void;
  travellersDetail: any;
  traceId?: string;
  inboundCommission?: number;
  outboundCommission?: number;
}

const Badge = ({
  borderColor,
  text,
}: {
  borderColor: string;
  text: string;
}) => (
  <span
    className="inline-block px-2 py-1 text-xs rounded"
    style={{
      border: `1px solid ${borderColor}`,
      color: borderColor,
      backgroundColor: `${borderColor}10`,
    }}
  >
    {text}
  </span>
);

// Main component that accepts flight data as props
const IntlRTFlightListCard: React.FC<FlightCardProps> = ({
  flightData,
  isActive,
  onToggle,
  travellersDetail,
  traceId,
  inboundCommission,
  outboundCommission,
}) => {
  const navigation = useNavigate();
  const data = flightData;

  const { userInfo } = useGlobalState()
  const currency = userInfo?.currencyCode || "AED";

  const [tabValue, setTabValue] = useState("1");
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (isActive && tabValue === "") setTabValue("1");
  }, [isActive, tabValue]);

  // Extract outbound and inbound flights
  const outboundFlight = data.Bonds?.find(
    (bond: any) => bond.BoundType === "OutBound"
  );
  const inboundFlight = data.Bonds?.find(
    (bond: any) => bond.BoundType === "InBound"
  );

  const outboundLeg = outboundFlight?.Legs?.[0];
  const inboundLeg = inboundFlight?.Legs?.[0];

  // Helper function to format date and time
  const formatDateTime = (date: string, time: string) => {
    if (!date || !time) return { date: "N/A", time: "N/A" };
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
    });
    return { date: formattedDate, time: time };
  };

  // Get airline logo URL
  const getAirlineLogoUrl = (airlineCode: string) => {
    return `https://images.ixigo.com/img/common-resources/airline-new/${airlineCode}.png`;
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const { commission, finalFare, ruleId } = useCommission(
    flightData,
    travellersDetail
  );

  const tdeatil = {
    ...travellersDetail,
    adultFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 0)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    childFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 1)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    infantFare:
      flightData?.Fare?.PaxFares?.filter((i: any) => i?.PaxType === 2)?.reduce(
        (acc: number, curr: any) => acc + (curr?.BasicFare ?? 0),
        0
      ) ?? 0,

    totalFare: flightData?.Fare?.TotalFareWithOutMarkUp ?? 0,
    totalTax: flightData?.Fare?.TotalTaxWithOutMarkUp ?? 0,
  };

  const totalFlightFare = commission + Number(tdeatil.totalFare);

  const { fareDetails: dynamicFareRules, emtFee } = parseDynamicFareRules(
    flightData?.FareRule
  );

  return (
    <div className="card mb-3">
      <div className="">
        <div className="row align-items-center p-3 pb-0">
          {/* Flight Details */}
          <div className="col-lg-10">
            <div className="row">
              {/* Onward Flight */}
              <div className="col-md-6 border-right">
                <div className="d-flex align-items-center mb-2">
                  <h6 className="mb-0 me-2 fw-bold ">Onward -</h6>
                  <span className=" small">{outboundLeg?.FlightName}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                    <img
                      src={getAirlineLogoUrl(outboundLeg?.CarrierCode)}
                      alt={outboundLeg?.FlightName}
                      className="w-8 h-8"
                    />
                  </div>

                  <div className="text-center">
                    <div className="h4 mb-0 fw-bold">
                      {
                        formatDateTime(
                          outboundLeg?.DepartureDate,
                          outboundLeg?.DepartureTime
                        ).time
                      }
                    </div>
                    <small className="">{outboundLeg?.Origin}</small>
                    <div className="text-xs text-muted">
                      T{outboundLeg?.DepartureTerminal}
                    </div>
                  </div>

                  <div className="flex-1 mx-4 text-center">
                    <small className=" ">{outboundLeg?.Duration}</small>
                    <div className="relative my-2">
                      <hr className="" />
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2 light-color">
                        <i className="fas fa-plane light-color"></i>
                      </div>
                    </div>
                    <small className="">
                      {outboundFlight?.Legs?.length - 1 === 0
                        ? "Non-Stop"
                        : outboundFlight?.Legs?.length - 1 + " Stops"}
                    </small>
                  </div>

                  <div className="text-center">
                    <div className="h4 mb-0 fw-bold">
                      {
                        formatDateTime(
                          outboundLeg?.ArrivalDate,
                          outboundLeg?.ArrivalTime
                        ).time
                      }
                    </div>
                    <small className="">
                      {
                        outboundFlight?.Legs?.[outboundFlight?.Legs?.length - 1]
                          ?.Destination
                      }
                    </small>
                    <div className="text-xs text-muted">
                      T
                      {
                        outboundFlight?.Legs?.[outboundFlight?.Legs?.length - 1]
                          ?.ArrivalTerminal
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Return Flight */}
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-2">
                  <h6 className="mb-0 me-2 fw-bold">Return -</h6>
                  <span className=" text-sm">{inboundLeg?.FlightName}</span>
                </div>

                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                    <img
                      src={getAirlineLogoUrl(inboundLeg?.CarrierCode)}
                      alt={inboundLeg?.FlightName}
                      className="w-8 h-8"
                    />
                  </div>

                  <div className="text-center">
                    <div className="h4 mb-0 fw-bold">
                      {
                        formatDateTime(
                          outboundFlight?.Legs?.[
                            outboundFlight?.Legs?.length - 1
                          ]?.ArrivalDate,
                          outboundFlight?.Legs?.[
                            outboundFlight?.Legs?.length - 1
                          ]?.ArrivalTime
                        ).time
                      }
                    </div>
                    <small className="">{inboundLeg?.Origin}</small>
                    <div className="text-xs text-muted">
                      T{inboundLeg?.DepartureTerminal}
                    </div>
                  </div>

                  <div className="flex-1 mx-4 text-center">
                    <small className=" block">{inboundLeg?.Duration}</small>
                    <div className="relative my-2">
                      <hr className="" />
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2 light-color">
                        <i className="fas fa-plane text-blue-500"></i>
                      </div>
                    </div>
                    <small className="">
                      {inboundFlight?.Legs?.length - 1 === 0
                        ? "Non-Stop"
                        : inboundFlight?.Legs?.length - 1 + " Stops"}
                    </small>
                  </div>

                  <div className="text-center">
                    <div className="h4 mb-0 fw-bold">
                      {
                        formatDateTime(
                          inboundFlight?.Legs?.[inboundFlight?.Legs?.length - 1]
                            ?.ArrivalDate,
                          inboundFlight?.Legs?.[inboundFlight?.Legs?.length - 1]
                            ?.ArrivalTime
                        ).time
                      }
                    </div>
                    <small className="">
                      {
                        inboundFlight?.Legs?.[inboundFlight?.Legs?.length - 1]
                          ?.Destination
                      }
                    </small>
                    <div className="text-xs text-muted">
                      T
                      {
                        inboundFlight?.Legs?.[inboundFlight?.Legs?.length - 1]
                          ?.ArrivalTerminal
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Price and Book Section */}
          <div className="col-lg-2 text-lg-center mt-4">
            <h3 className=" mb-2 fw-bold text-2xl">{currency} {totalFlightFare}</h3>

            <div className="d-grid gap-2">
              <button
                className="book-btn"
                onClick={() =>
                  navigation("/flightDetail", {
                    state: {
                      inBoundData: flightData,
                      outBoundData: flightData,
                      travellersDetail: tdeatil,
                      traceId: traceId,
                      ruleId,
                      commission:
                        (inboundCommission ?? 0) + (outboundCommission ?? 0),
                    },
                  })
                }
              >
                Book
              </button>
            </div>
          </div>
        </div>

        {/* Flight Details Toggle */}
        <div className="row">
          <div className="col-12">
            <div className="flight-detail p-1">
              <span
                onClick={() => {
                  onToggle();
                  setTabValue("1");
                }}
                className=" ps-2 pb-1"
              >
                Flight Details <KeyboardArrowDownIcon />
              </span>
            </div>
          </div>

          {/* Accordion Content */}
          {isActive && (
            <div className="">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <Box sx={{ width: "100%", typography: "body1" }}>
                  <TabContext value={tabValue}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <TabList
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        onChange={handleChange}
                        aria-label="static tabs example"
                      >
                        <Tab label="Flight Information" value="1" />
                        <Tab label="Fare Details" value="2" />
                        <Tab label="Baggage Information" value="3" />
                        <Tab label="Cancellation & Change Rule" value="4" />
                      </TabList>
                    </Box>

                    {/* Tab Panels */}
                    {/* Flight Information */}
                    <TabPanel value="1">
                      {/* Outbound Flight Details */}
                      <div className="card p-3">
                        {outboundFlight?.Legs?.length > 0 &&
                          outboundFlight?.Legs?.map(
                            (leg: any, index: number) => (
                              <>
                                <div className="mb-6" key={index}>
                                  <h5 className="mb-3 flex items-center">
                                    <span>{leg?.Origin}</span>
                                    <i className="fas fa-arrow-right px-2"></i>
                                    <span>{leg?.Destination}</span>
                                  </h5>

                                  <div className="block lg:flex items-center gap-5 pl-5">
                                    <div className="w-8 h-8 flex-shrink-0">
                                      <img
                                        src={getAirlineLogoUrl(
                                          leg?.CarrierCode
                                        )}
                                        alt={leg?.FlightName}
                                        className="w-full h-full"
                                      />
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className=" font-medium text-gray-900">
                                        {leg?.FlightName}
                                      </div>
                                      <span className="text-sm">
                                        {leg?.CarrierCode} -{" "}
                                        {leg?.FlightNumber?.trim()}
                                      </span>
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className="h4 mb-0 fw-bold">
                                        {leg?.DepartureTime}
                                      </div>
                                      <small className="">{leg?.Origin}</small>
                                    </div>

                                    <div className=" text-center mx-0 mx-sm-4 min-w-60">
                                      <small className=" ">
                                        {leg?.Duration}
                                      </small>
                                      <div className="relative my-2">
                                        <hr className="text-muted" />
                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2 light-color">
                                          <i className="fas fa-plane text-blue-500"></i>
                                        </div>
                                      </div>
                                      {/* <small className="">
                                  {outboundFlight?.Legs?.length - 1 === 0
                                    ? "Non-Stop"
                                    : outboundFlight?.Legs?.length -
                                      1 +
                                      " Stops"}
                                </small> */}
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className="h4 mb-0 fw-bold">
                                        {leg?.ArrivalTime}
                                      </div>
                                      <small className="">
                                        {leg?.Destination}
                                      </small>
                                    </div>
                                  </div>
                                  {/* 👉 Show Layover if not last leg */}
                                  {index < outboundFlight?.Legs?.length - 1 && (
                                    <div className=" my-3">
                                      <div className="layover float-none ng-scope">
                                        <span>
                                          <strong className="ng-binding">
                                            {leg?.LayoverDuration}
                                          </strong>
                                          Layover in
                                          <strong className="ng-binding">
                                            {" "}
                                            {leg?.LayoverAt}
                                          </strong>
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )
                          )}
                      </div>
                      <hr className="my-2" />

                      {/* Return Flight Details */}

                      <div className="card p-3">
                        {inboundFlight?.Legs?.length > 0 &&
                          inboundFlight?.Legs?.map(
                            (leg: any, index: number) => (
                              <>
                                <div key={index}>
                                  <h5 className="mb-3 flex items-center">
                                    <span>{leg?.Origin}</span>
                                    <i className="fas fa-arrow-right px-2 "></i>
                                    <span>{leg?.Destination}</span>
                                  </h5>

                                  <div className="block lg:flex items-center gap-5 pl-5">
                                    <div className="w-8 h-8 flex-shrink-0">
                                      <img
                                        src={getAirlineLogoUrl(
                                          leg?.CarrierCode
                                        )}
                                        alt={leg?.FlightName}
                                        className="w-full h-full"
                                      />
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className=" font-medium text-gray-900">
                                        {leg?.FlightName}
                                      </div>
                                      <span className="text-sm">
                                        {leg?.CarrierCode} -{" "}
                                        {leg?.FlightNumber?.trim()}
                                      </span>
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className="h4 mb-0 fw-bold">
                                        {leg?.DepartureTime}
                                      </div>
                                      <small className="">{leg?.Origin}</small>
                                    </div>

                                    <div className=" text-center mx-0 mx-sm-4 min-w-60">
                                      <small className=" ">
                                        {leg?.Duration}
                                      </small>
                                      <div className="relative my-2">
                                        <hr className="text-muted" />
                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2 light-color">
                                          <i className="fas fa-plane text-blue-500"></i>
                                        </div>
                                      </div>
                                      {/* <small className="">
                                  {inboundFlight?.Legs?.length - 1 === 0
                                    ? "Non-Stop"
                                    : inboundFlight?.Legs?.length -
                                      1 +
                                      " Stops"}
                                </small> */}
                                    </div>

                                    <div className="text-center min-w-20">
                                      <div className="h4 mb-0 fw-bold">
                                        {leg?.ArrivalTime}
                                      </div>
                                      <small className="">
                                        {leg?.Destination}
                                      </small>
                                    </div>
                                  </div>
                                  {index < inboundFlight?.Legs?.length - 1 && (
                                    <div className=" my-3">
                                      <div className="layover float-none ng-scope">
                                        <span>
                                          <strong className="ng-binding">
                                            {leg?.LayoverDuration}
                                          </strong>
                                          Layover in
                                          <strong className="ng-binding">
                                            {" "}
                                            {leg?.LayoverAt}
                                          </strong>
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )
                          )}
                      </div>
                    </TabPanel>
                    {/* Fare Details */}
                    <TabPanel value="2">
                      <div className="da_adultsection mb-3">
                        {/* <div className="float-left w-100 pass_head text-center">
                      <span className="fnt-wt-600">
                        <i className="fa-regular fa-user"></i> 1 Adult(s)
                      </span>
                    </div> */}
                        <div className="float-left w-100 brdr-radius-5 mg-btm-20">
                          <div className="fare-card">
                            <div className="fare-item">
                              <span>{tdeatil?.adultCount} x Adult</span>
                              <span>
                                {currency} {tdeatil?.adultFare * tdeatil?.adultCount}
                              </span>
                            </div>
                            <div className="fare-item">
                              <span>{tdeatil?.childrenCount} x Child</span>
                              <span>
                                {currency}{" "}
                                {tdeatil?.childFare * tdeatil?.childrenCount}
                              </span>
                            </div>
                            <div className="fare-item">
                              <span>{tdeatil?.infantCount} x Infant</span>
                              <span>
                                {currency} {tdeatil?.infantFare * tdeatil?.infantCount}
                              </span>
                            </div>

                            <div className="fare-item">
                              <span>Total Tax</span>
                              <span>{currency} {tdeatil.totalTax}</span>
                            </div>
                            <div className="fare-item">
                              <span>other</span>
                              <span>{currency} {commission}</span>
                            </div>
                            <div className="fare-item total">
                              <span>Total (Fee & Surcharge)</span>
                              <span>{currency} {totalFlightFare}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                    {/* Baggage Information */}
                    <TabPanel value="3">
                      <div className="row">
                        <div
                          className="accordion col-lg-6"
                          id="accordionExample"
                        >
                          <div id="collapseTwo">
                            <div className="baggagerule mgt15 mar-to-12">
                              <div style={{ overflowX: "auto", width: "100%" }}>
                                <table width="100%" rules="all" border={1}>
                                  <tbody>
                                    <tr>
                                      <th>
                                        <p className="frtbl_hd">Airline</p>
                                      </th>
                                      <th>
                                        <p className="frtbl_hd">
                                          Check-in Baggage
                                        </p>
                                      </th>
                                      <th>
                                        <p className="frtbl_hd">
                                          Cabin Baggage
                                        </p>
                                      </th>
                                    </tr>
                                    {outboundFlight?.Legs?.map(
                                      (leg: any, index: number) => (
                                        <tr
                                          ng-repeat="baggage in BaggageModel"
                                          className="ng-scope"
                                          key={index}
                                        >
                                          <td>
                                            <div
                                              className="flgi-l"
                                              style={{
                                                float: "left",
                                                width: 32,
                                                marginRight: 10,
                                              }}
                                            >
                                              <img
                                                src={getAirlineLogoUrl(
                                                  leg?.CarrierCode
                                                )}
                                                alt={leg?.FlightName}
                                              />
                                            </div>
                                            <div
                                              style={{
                                                float: "left",
                                                fontSize: 11,
                                              }}
                                            >
                                              <p
                                                ng-bind="baggage.segDTL | split:'|':0"
                                                className="ng-binding"
                                              >
                                                {leg.FlightName}
                                              </p>
                                              <p className="ng-binding">
                                                {leg.CarrierCode}
                                                {"-"} {leg.FlightNumber}
                                              </p>
                                            </div>
                                          </td>
                                          <td className="ng-binding">
                                            {leg.BaggageWeight}Kg
                                          </td>

                                          <td className="ng-binding ng-scope">
                                            {leg.CabinBagWT}kg
                                          </td>
                                        </tr>
                                      )
                                    )}

                                    <tr>
                                      <td colSpan={3}>
                                        <div className="termsbagg">
                                          <ul>
                                            <li>
                                              Baggage information mentioned
                                              above is obtained from airline's
                                              reservation system, EWTS does not
                                              guarantee the accuracy of this
                                              information.
                                            </li>
                                            <li>
                                              The baggage allowance may vary
                                              according to stop-overs,
                                              connecting flights. changes in
                                              airline rules. etc.
                                            </li>
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="accordion col-lg-6"
                          id="accordionExample"
                        >
                          <div id="collapseTwo">
                            <div className="baggagerule mgt15 mar-to-12">
                              <div style={{ overflowX: "auto", width: "100%" }}>
                                <table width="100%" rules="all" border={1}>
                                  <tbody>
                                    <tr>
                                      <th>
                                        <p className="frtbl_hd">Airline</p>
                                      </th>
                                      <th>
                                        <p className="frtbl_hd">
                                          Check-in Baggage
                                        </p>
                                      </th>
                                      <th>
                                        <p className="frtbl_hd">
                                          Cabin Baggage
                                        </p>
                                      </th>
                                    </tr>
                                    {inboundFlight?.Legs?.map(
                                      (leg: any, index: number) => (
                                        <tr
                                          ng-repeat="baggage in BaggageModel"
                                          className="ng-scope"
                                          key={index}
                                        >
                                          <td>
                                            <div
                                              className="flgi-l"
                                              style={{
                                                float: "left",
                                                width: 32,
                                                marginRight: 10,
                                              }}
                                            >
                                              <img
                                                src={getAirlineLogoUrl(
                                                  leg?.CarrierCode
                                                )}
                                                alt={leg?.FlightName}
                                              />
                                            </div>
                                            <div
                                              style={{
                                                float: "left",
                                                fontSize: 11,
                                              }}
                                            >
                                              <p
                                                ng-bind="baggage.segDTL | split:'|':0"
                                                className="ng-binding"
                                              >
                                                {leg.FlightName}
                                              </p>
                                              <p className="ng-binding">
                                                {leg.CarrierCode}
                                                {"-"} {leg.FlightNumber}
                                              </p>
                                            </div>
                                          </td>
                                          <td className="ng-binding">
                                            {leg.BaggageWeight}Kg
                                          </td>

                                          <td className="ng-binding ng-scope">
                                            {leg.CabinBagWT}kg
                                          </td>
                                        </tr>
                                      )
                                    )}

                                    <tr>
                                      <td colSpan={3}>
                                        <div className="termsbagg">
                                          <ul>
                                            <li>
                                              Baggage information mentioned
                                              above is obtained from airline's
                                              reservation system, EWTS does not
                                              guarantee the accuracy of this
                                              information.
                                            </li>
                                            <li>
                                              The baggage allowance may vary
                                              according to stop-overs,
                                              connecting flights. changes in
                                              airline rules. etc.
                                            </li>
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                    {/* Cancellation & Change Rules */}
                    <TabPanel value="4">
                      <div className="card card-grid h-full min-w-full">
                        <div className="card-header">
                          <h3 className="card-title">Fare Rules</h3>
                          <div className="refund-text">
                            <Badge borderColor="#2dca1c" text="Refundable" />
                          </div>
                        </div>
                        <div className="card-body">
                          <div
                            data-datatable="true"
                            data-datatable-page-size={5}
                            className="datatable-initialized"
                          >
                            <div style={{ overflowX: "auto", width: "100%" }}>
                              <table
                                className="table table-border pt-2"
                                data-datatable-table="true"
                              >
                                <thead>
                                  <tr>
                                    <th>
                                      <span className="sort asc">
                                        <span className="sort-label">
                                          Timeframe to Cancel
                                        </span>
                                      </span>
                                    </th>
                                    <th>
                                      <span className="sort">
                                        <span className="sort-label">
                                          Rating
                                        </span>
                                      </span>
                                    </th>
                                    <th>
                                      <span className="sort">
                                        <span className="sort-label">
                                          Last Modified
                                        </span>
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dynamicFareRules.map(
                                    ({ start, end, cancelFee }) => (
                                      <tr key={`cancel-${start}-${end}`}>
                                        <td>
                                          Cancel before {end} hours of departure
                                        </td>
                                        <td>
                                          <div className="flex flex-col gap-2">
                                            <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                              {currency} {cancelFee ?? 0}
                                            </a>
                                          </div>
                                        </td>
                                        <td>
                                          <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                            {currency} {emtFee}
                                          </a>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div
                            data-datatable="true"
                            data-datatable-page-size={5}
                            className="datatable-initialized"
                          >
                            <div style={{ overflowX: "auto", width: "100%" }}>
                              <table
                                className="table table-border pt-2"
                                data-datatable-table="true"
                              >
                                <thead>
                                  <tr>
                                    <th style={{ width: "562px" }}>
                                      <span className="sort asc">
                                        <p className="sort-label">
                                          Timeframe to Reschedule
                                        </p>
                                      </span>
                                    </th>
                                    <th>
                                      <span className="sort">
                                        <span className="sort-label">
                                          Airline Fees
                                        </span>
                                      </span>
                                    </th>
                                    <th>
                                      <span className="sort">
                                        <span className="sort-label">
                                          EWTS Fees
                                        </span>
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dynamicFareRules?.map(
                                    ({ start, end, changeFee }) => (
                                      <tr key={`change-${start}-${end}`}>
                                        <td>
                                          Reschedule before {end} hours of
                                          departure
                                        </td>
                                        <td>
                                          <div className="flex flex-col gap-2">
                                            <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                              {currency} {changeFee ?? 0}
                                            </a>
                                          </div>
                                        </td>
                                        <td>
                                          <a className="leading-none font-medium text-sm text-gray-900 hover:text-primary">
                                            {currency} {emtFee}
                                          </a>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12 col-sm-12 col-xs-12 terms-h ms-4 mb-2">
                              Terms &amp; Conditions
                            </div>
                            <div className="col-md-12 col-sm-12 col-xs-12">
                              <div className="terms mar10">
                                <ul style={{ display: "none" }}>
                                  {/*li>Penalty is subject to 4 hours prior to departure and no changes are allowed after that.</li*/}
                                  <li>
                                    The charges will be on per passenger per
                                    sector
                                  </li>
                                  <li>
                                    Rescheduling Charges = Rescheduling/Change
                                    Penalty + Fare Difference (if applicable)
                                  </li>
                                  <li>
                                    Partial cancellation is not allowed on the
                                    flight tickets which are book under special
                                    discounted fares
                                  </li>
                                  <li>
                                    In case, the customer have not cancelled the
                                    ticket within the stipulated time or no show
                                    then only statutory taxes are refundable
                                    from the respective airlines
                                  </li>
                                  <li>
                                    For infants there is no baggage allowance
                                  </li>
                                  <li>
                                    In certain situations of restricted cases,
                                    no amendments and cancellation is allowed
                                  </li>
                                  <li>
                                    Penalty from airlines needs to be
                                    reconfirmed before any cancellation or
                                    amendments
                                  </li>
                                  <li>
                                    Penalty changes in airline are indicative
                                    and can be changed without any prior notice
                                  </li>
                                </ul>
                                <ul>
                                  <li className="ng-binding">
                                    Total Rescheduling Charges Airlines
                                    Rescheduling fees Fare Difference if
                                    applicable + EWTS Fees.
                                  </li>
                                  <li>
                                    The airline cancel reschedule fees is
                                    indicative and can be changed without any
                                    prior notice by the airlines..
                                  </li>
                                  <li>
                                    EaseMyTrip does not guarantee the accuracy
                                    of cancel reschedule fees.
                                  </li>
                                  <li>
                                    Partial cancellation is not allowed on the
                                    flight tickets which are book under special
                                    round trip discounted fares.
                                  </li>
                                  <li>
                                    Airlines doesnt allow any additional baggage
                                    allowance for any infant added in the
                                    booking
                                  </li>
                                  <li>
                                    In certain situations of restricted cases,
                                    no amendments and cancellation is allowed
                                  </li>
                                  <li>
                                    Airlines cancel reschedule should be
                                    reconfirmed before requesting for a
                                    cancellation or amendment
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                  </TabContext>
                </Box>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntlRTFlightListCard;
